import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, matchPath } from 'react-router-dom';
import { AuthProvider, useAuth, getHomePath } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { TenantDataProvider } from './context/TenantDataContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingState from './components/LoadingState';
import { PremiumGate } from './components/LicenseGate';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SetupAccountPage from './pages/SetupAccountPage';
import AdminLayout from './pages/AdminLayout';
import Tenants from './pages/Tenants';
import Users from './pages/Users';
import CustomerPage from './pages/CustomerPage';
import SupportPage from './pages/SupportPage';
import LicensesPage from './pages/LicensesPage';
import NewsAdmin from './pages/NewsAdmin';
import Organizations from './pages/Organizations';
import ActivityLog from './pages/ActivityLog';
import TenantLayout from './pages/TenantLayout';
import Oversigt from './pages/Oversigt';
import WorkQueuePage from './pages/WorkQueuePage';
import ConfigurationPage from './pages/ConfigurationPage';
import CompliancePage from './pages/CompliancePage';
import IntuneAppsPage from './pages/IntuneAppsPage';
import AuditReport from './pages/AuditReport';
import ComingSoon from './pages/ComingSoon';
import History from './pages/History';
import ItListPage from './pages/ItListPage';
import DecisionsRisks from './pages/DecisionsRisks';
import DataProtection from './pages/DataProtection';
import ReportRender from './pages/ReportRender';
import LegalPage from './pages/LegalPage';
import privacyContent from './content/legal/privacy.da';
import cookiesContent from './content/legal/cookies.da';

import './App.css';
import './typescale.css';

// The old per-area list pages and their object-detail drawers were removed
// — Arbejdskø is the one place objects are reviewed and documented. Their
// addresses still redirect there (to the matching area tab), so old
// bookmarks and links land somewhere real instead of the landing page.
const AREA_REDIRECTS = {
  identity: 'identity',
  'guest-users': 'identity',
  roles: 'roles',
  'conditional-access': 'conditional-access',
  devices: 'devices',
  security: 'security',
  applications: 'applications',
  teams: 'sharepoint',
  sharepoint: 'sharepoint',
  exchange: 'exchange',
  groups: 'sharepoint',
  licenses: 'licenses',
};

function RedirectToArea({ area }) {
  const { tenantId } = useParams();
  return <Navigate to={`/tenant/${tenantId}/work-queue?area=${area}`} replace />;
}

// Preserves query params (org context, ?tenant_added=true) across the
// redirect — a plain <Navigate to="/tenants"> would silently drop them.
function RedirectToTenants() {
  const location = useLocation();
  return <Navigate to={`/tenants${location.search}`} replace />;
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

function AppContent() {
  const { user, loading, role, roleLoading, tenantIds } = useAuth();
  const location = useLocation();

  // `loading` (MSAL resolving `user`) and `roleLoading` (the separate
  // /api/me fetch) finish independently — user resolves first. Computing
  // homePath below while a logged-in user's role is still null sends every
  // first-time login through getHomePath's generic '/tenants' fallback
  // instead of their real role's portal, so this has to wait on both.
  if (loading || (user && roleLoading)) {
    return (
      <LoadingState />
    );
  }

  const homePath = getHomePath(role, tenantIds);
  const tenantMatch = matchPath('/tenant/:tenantId/*', location.pathname);

  const mainRoutes = (
    <Routes>
      {/* Always the marketing page — an already-authenticated visitor typing
          nordcomply.dk (or coming back to it later) should still see the
          site, not get silently bounced into the app. /login is still the
          entry point that carries a logged-in user straight to homePath. */}
      <Route path="/" element={<LandingPage />} />
      {/* The public text pages: open to everyone, also signed out. */}
      <Route path="/legal/privacy" element={<LegalPage content={privacyContent} />} />
      <Route path="/legal/cookies" element={<LegalPage content={cookiesContent} />} />
      <Route path="/login" element={user ? <Navigate to={homePath} /> : <LoginPage />} />
      {/* Reached from the setup-invite email — never gated behind
          ProtectedRoute, since there's no session yet at this point. */}
      <Route path="/setup-account" element={<SetupAccountPage />} />
      {/* The server's own browser opens this to make the PDF for a report mail. It signs in with a short-lived read-only
          token in the address, not with a login (see pages/ReportRender.jsx). */}
      <Route path="/report-render/:tenantId" element={<ReportRender />} />

      {/* Tenant Portal — every route here shares the AdminLayout sidebar shell.
          Used by both 'admin' (an MSP's own staff, one org) and 'provider'
          (NordComply's team, drilled into a specific org) roles. */}
      <Route element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
        {/* Dashboard was a second, redundant "pick a tenant" view — Tenants now
            covers everything it did (KPI row + toast), so old links/bookmarks
            just redirect through instead of 404ing. */}
        <Route path="/dashboard" element={<RedirectToTenants />} />
        {/* "Tilføj tenant" is a modal opened from Tenants/AdminLayout now
            (matching the mockup) rather than its own page — old bookmarks
            just redirect through. */}
        <Route path="/admin" element={<RedirectToTenants />} />
        <Route path="/tenants" element={<Tenants />} />
        <Route path="/customer/:tenantId" element={<CustomerPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/users" element={<Users />} />
      </Route>

      {/* Provider Portal — NordComply-only, one tier above the Tenant Portal above */}
      <Route element={<ProtectedRoute providerOnly><AdminLayout /></ProtectedRoute>}>
        <Route path="/organizations" element={<Organizations />} />
        <Route path="/activity-log" element={<ActivityLog />} />
        <Route path="/licenses" element={<LicensesPage />} />
        <Route path="/support-admin" element={<SupportPage editable />} />
        <Route path="/news" element={<NewsAdmin />} />
      </Route>

      {/* Customer Portal — a single tenant's own dashboard */}
      <Route path="/tenant/:tenantId" element={<ProtectedRoute><TenantLayout /></ProtectedRoute>}>
        <Route index element={<Oversigt />} />
        <Route path="work-queue" element={<WorkQueuePage />} />
        <Route path="configuration" element={<ConfigurationPage />} />
        {/* Indstillinger was renamed Konfiguration — the old path still lands on it. */}
        <Route path="settings" element={<Navigate to="../configuration" replace />} />
        <Route path="compliance" element={<CompliancePage />} />
        {/* Kontroller was renamed Compliance — the old path still lands on it. */}
        <Route path="controls" element={<Navigate to="../compliance" replace />} />
        {/* Politikker was split into Indstillinger (raw setup) and Kontroller (compliance status) —
            old links still land somewhere real instead of 404ing. */}
        <Route path="policies" element={<Navigate to="../configuration" replace />} />
        {/* Folded into the Work Queue's own "Alle objekter" mode — old links
            still land somewhere real instead of 404ing. */}
        <Route path="detailed" element={<Navigate to="../work-queue" replace />} />
        {/* V7: both folded into Mit overblik's "Kræver opmærksomhed" section */}
        <Route path="action" element={<Navigate to=".." replace />} />
        <Route path="security-status" element={<Navigate to=".." replace />} />
        <Route path="data-protection" element={<DataProtection />} />
        <Route path="membership" element={<RedirectToArea area="sharepoint" />} />
        <Route path="intune-apps" element={<IntuneAppsPage />} />
        {Object.entries(AREA_REDIRECTS).flatMap(([path, area]) => [
          <Route key={path} path={path} element={<RedirectToArea area={area} />} />,
          <Route key={`${path}/*`} path={`${path}/*`} element={<RedirectToArea area={area} />} />,
        ])}
        <Route path="copilot" element={<ComingSoon title="Copilot & AI" description="Overvågning af Copilot-brug kommer snart." />} />
        {/* The three Dokumentation pages are Premium: a Standard tenant sees the lock screen instead. */}
        <Route path="audit" element={<PremiumGate page="audit"><AuditReport /></PremiumGate>} />
        <Route path="decisions" element={<PremiumGate page="decisions"><DecisionsRisks /></PremiumGate>} />
        <Route path="history" element={<History />} />
        {/* IT-opgaver is open for every licence: the button that fills it is in Gennemgang, which every licence has. */}
        <Route path="it-list" element={<ItListPage />} />

        {/* Legacy Danish-language paths — every area route above was
            renamed to English (see CLAUDE.md). Old bookmarks/browser
            history for a real, already-in-use tenant should still land
            on the right page instead of bouncing to the landing page,
            same treatment as the retired-route redirects above. */}
        <Route path="arbejdsko" element={<Navigate to="../work-queue" replace />} />
        <Route path="identitet" element={<Navigate to="../identity" replace />} />
        <Route path="gaestebrugere" element={<Navigate to="../guest-users" replace />} />
        <Route path="roller" element={<Navigate to="../roles" replace />} />
        <Route path="enheder" element={<Navigate to="../devices" replace />} />
        <Route path="sikkerhed" element={<Navigate to="../security" replace />} />
        <Route path="applikationer" element={<Navigate to="../applications" replace />} />
        <Route path="grupper" element={<Navigate to="../groups" replace />} />
        <Route path="licenser" element={<Navigate to="../licenses" replace />} />
        <Route path="beslutninger" element={<Navigate to="../decisions" replace />} />
        <Route path="historik" element={<Navigate to="../history" replace />} />
        <Route path="databeskyttelse" element={<Navigate to="../data-protection" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );

  if (matchPath('/report-render/:tenantId', location.pathname)) return mainRoutes;

  if (!tenantMatch) {
    return mainRoutes;
  }

  return (
    <TenantDataProvider tenantId={tenantMatch.params.tenantId}>
      {mainRoutes}
    </TenantDataProvider>
  );
}

export default App;
